import { useTranslation } from 'react-i18next';
import {
  PMO_FLOW_CLASS_COLORS,
  buildPhaseBarSlices,
  buildPhaseBoundaryTicks,
  formatQuantile,
  type PmoFlowBarSlice,
  type PmoFlowBoundaryTick,
  type PmoFlowHighlightProps,
} from './segment-utils.js';
import type { PmoFlowBoundaryKey } from './segment-utils.js';
import type { CSSProperties } from 'react';
import type { PmoFlowSegmentStat } from '../../../types/pmo-flow.js';

/** バー本体の高さ。細めにして目盛りとの主従を付ける。 @graph-connects none */
const BAR_HEIGHT_CLASS = 'h-7';

/** 工程名ラベル 1 段分の高さ。バーの上下に同じ高さで確保する。 @graph-connects none */
const LABEL_AREA_CLASS = 'h-6';

/** 区間の間に空ける背景色の隙間（px）。 @graph-connects none */
const SLICE_GAP_PX = 2;

/** 極端に短い区間でも境界が見える最小幅（px）。 @graph-connects none */
const SLICE_MIN_WIDTH_PX = 10;

/** バー両端の角丸（px）。区間どうしが接する内側の角は丸めない。 @graph-connects none */
const SLICE_RADIUS_PX = 6;

/** 区間内に日数を書き込む最小の幅比率。これ未満は文字が入らないので省く。 @graph-connects none */
const INLINE_VALUE_MIN_RATIO = 0.07;

/** 目盛り線の長さ（px）。 @graph-connects none */
const TICK_LINE_PX = 6;

/**
 * 工程名をバーの上下どちらに置くか。
 * 結合テスト開始とデザインチェック開始を上に逃がすのは、実測でテスト実施の中央値が
 * 0〜2 日しかなく、その前後の境界がほぼ同じ位置に重なるため。
 * 要件定義完了を上に逃がすのも同じ理由で、要件定義区間は未計測のあいだ公称幅
 * （計測済み合計の 8%）しか持たず、左端の 要件定義assign とラベルが重なる。
 * 残りは下に揃えた方が 要件定義assign から順に左から読める
 * @graph-connects none
 */
const TICK_PLACEMENT_BY_BOUNDARY: Record<PmoFlowBoundaryKey, 'above' | 'below'> = {
  reqAssign: 'below',
  reqDone: 'above',
  assign: 'below',
  start: 'below',
  devDone: 'below',
  testStart: 'above',
  integDone: 'below',
  designCheckStart: 'above',
  designCheckDone: 'below',
};

/** 種別ごとの区間クラス。実測のみ区間色で塗る。 @graph-connects none */
const SLICE_CLASS_BY_KIND: Record<PmoFlowBarSlice['kind'], string> = {
  measured: 'pmo-phase-slice',
  pending: 'pmo-phase-slice--pending',
  illustrative: 'pmo-phase-slice--illustrative',
};

/**
 * 区間の位置に応じた border-radius。バーの外側（先頭の左端・末尾の右端）だけ丸め、
 * 区間どうしが接する内側は直角にして、1 本の帯が 2px の隙間で割れているように見せる。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズ積み上げバーの区間ごとの角丸（両端のみ丸める）を算出する
 * @graph-connects none
 */
function sliceRadius(index: number, total: number): string {
  const left = index === 0 ? SLICE_RADIUS_PX : 0;
  const right = index === total - 1 ? SLICE_RADIUS_PX : 0;
  return `${String(left)}px ${String(right)}px ${String(right)}px ${String(left)}px`;
}

export interface PhaseCompositionBarProps extends PmoFlowHighlightProps {
  /** 区間統計（並べ替え・全体スパンの除外はこのコンポーネント内で行う） */
  segments: PmoFlowSegmentStat[];
  /** 読み込み状態 */
  isLoading: boolean;
}

/**
 * 積み上げバーの 1 区間。区間色は CSS 変数 `--pmo-phase-color` で渡し、
 * 角丸・塗りは `styles.css` のクラス側に寄せる（フラット塗り）。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズ積み上げバーの1区間を区間色つきの角丸ブロックとして描画する
 * @graph-connects none
 */
function PhaseSlice({
  slice,
  label,
  value,
  borderRadius,
  isHighlighted,
  onHighlight,
}: {
  slice: PmoFlowBarSlice;
  label: string;
  value: string;
  borderRadius: string;
  isHighlighted: boolean;
  onHighlight: (key: PmoFlowBarSlice['key'] | null) => void;
}) {
  const style: CSSProperties = {
    flexGrow: slice.width,
    flexBasis: 0,
    minWidth: `${String(SLICE_MIN_WIDTH_PX)}px`,
    borderRadius,
    ['--pmo-phase-color' as string]: PMO_FLOW_CLASS_COLORS[slice.segmentClass],
  };

  return (
    <div
      className={`relative flex items-center overflow-hidden ${SLICE_CLASS_BY_KIND[slice.kind]}${
        isHighlighted ? ' pmo-phase-slice--active' : ''
      }`}
      style={style}
      title={slice.kind === 'measured' ? `${label} ${value}` : label}
      // hover に加えキーボード focus でも一覧側と連動させる（マウス専用にしない）
      tabIndex={0}
      aria-label={slice.kind === 'measured' ? `${label} ${value}` : label}
      data-testid={`pmo-flow-slice-${slice.key}`}
      data-kind={slice.kind}
      data-highlighted={String(isHighlighted)}
      onMouseEnter={() => {
        onHighlight(slice.key);
      }}
      onMouseLeave={() => {
        onHighlight(null);
      }}
      onFocus={() => {
        onHighlight(slice.key);
      }}
      onBlur={() => {
        onHighlight(null);
      }}
    >
      {slice.kind === 'measured' && slice.ratio >= INLINE_VALUE_MIN_RATIO && (
        <span className="px-2 text-[11px] font-medium tabular-nums text-white">{value}</span>
      )}
    </div>
  );
}

/**
 * 工程名の目盛り 1 本。境界位置に細い縦線を立て、その先に工程名を置く。
 * 目盛りは 1 本おきにバーの上下へ振り分けるので、隣り合うラベルが重ならない。
 * 左端は左寄せ、右端は右寄せにしてバーの外へはみ出さないようにする。
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business PMOフェーズ積み上げバーの境界位置に工程名の目盛りを上下へ振り分けて描画する
 * @graph-connects none
 */
function BoundaryTick({
  tick,
  labels,
  placement,
  align,
}: {
  tick: PmoFlowBoundaryTick;
  labels: string[];
  placement: 'above' | 'below';
  align: 'start' | 'center' | 'end';
}) {
  const translate = align === 'start' ? '0' : align === 'end' ? '-100%' : '-50%';
  const alignClass =
    align === 'start' ? 'items-start' : align === 'end' ? 'items-end' : 'items-center';
  const line = (
    <span
      aria-hidden="true"
      className="w-px bg-[var(--color-border)]"
      style={{ height: `${String(TICK_LINE_PX)}px` }}
    />
  );
  const text = labels.map((label) => (
    <span
      key={label}
      className="whitespace-nowrap text-[10px] leading-tight text-[var(--color-pmo-phase-label)]"
    >
      {label}
    </span>
  ));

  return (
    <div
      className={`absolute flex flex-col ${alignClass} ${
        placement === 'above' ? 'bottom-0 justify-end' : 'top-0'
      }`}
      style={{ left: `${String(tick.position * 100)}%`, transform: `translateX(${translate})` }}
      data-testid={`pmo-flow-boundary-${tick.boundaries.join('-')}`}
      data-placement={placement}
    >
      {placement === 'above' ? (
        <>
          {text}
          <span className="mt-0.5 flex flex-col items-center">{line}</span>
        </>
      ) : (
        <>
          {line}
          <span className="mt-0.5 flex flex-col">{text}</span>
        </>
      )}
    </div>
  );
}

/**
 * フェーズ構成の横積み上げバー。区間ごとの p50 日数を待ち＝青 / 作業＝橙の
 * ブロックで並べ、区間の間は 2px の背景色の隙間で切る。角丸はバーの両端だけで、
 * 内側は直角にして 1 本の帯としての連続感を出す。境界には工程名
 * （assign / 開発着手 / 開発完了 …）を目盛りとして立て、位置が近い境界だけ
 * バーの上へ逃がすことでラベル同士の衝突を避ける。
 *
 * 計測しないデザインチェックは工程の連なりが切れないようハッチの色帯で埋める。
 * 区間に hover すると `onHighlight` で区間キーを親へ通知し、一覧側の同じ区間が光る
 * （逆向きは `highlightedKey` で受ける）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズリードタイムの横積み上げバー。区間ごとのp50日数を待ち=青/作業=橙のフラットな角丸ブロックで積み上げ、境界に工程名の目盛りを上下へ振り分けて立てる
 * @graph-connects buildPhaseBarSlices [calls] 区間の幅・比率算出
 * @graph-connects buildPhaseBoundaryTicks [calls] 工程名目盛りの位置算出
 */
export function PhaseCompositionBar({
  segments,
  isLoading,
  highlightedKey = null,
  onHighlight,
}: PhaseCompositionBarProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div
        className={`${BAR_HEIGHT_CLASS} rounded-lg bg-[var(--color-bg-elevated)] animate-pulse`}
        data-testid="pmo-flow-composition-skeleton"
      />
    );
  }

  const slices = buildPhaseBarSlices(segments);

  if (slices.length === 0) {
    return (
      <div
        className={`${BAR_HEIGHT_CLASS} flex items-center justify-center rounded-lg border border-dashed border-[var(--color-border)]`}
      >
        <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
      </div>
    );
  }

  const ticks = buildPhaseBoundaryTicks(slices);
  const daysSuffix = t('kpi.pmoFlow.unit.daysSuffix');
  const renderTicks = (placement: 'above' | 'below') =>
    ticks.map((tick, index) =>
      // 表示しない工程を挟んで境界が 2 つ重なる場合は、先に来る方の配置に合わせる
      TICK_PLACEMENT_BY_BOUNDARY[tick.boundaries[0] ?? 'assign'] === placement ? (
        <BoundaryTick
          key={tick.boundaries.join('-')}
          tick={tick}
          labels={tick.boundaries.map((boundary) => t(`kpi.pmoFlow.boundary.${boundary}`))}
          placement={placement}
          align={index === 0 ? 'start' : index === ticks.length - 1 ? 'end' : 'center'}
        />
      ) : null,
    );

  return (
    <div data-testid="pmo-flow-composition-bar">
      <div className={`relative ${LABEL_AREA_CLASS}`}>{renderTicks('above')}</div>

      <div
        className={`flex ${BAR_HEIGHT_CLASS} w-full items-stretch`}
        style={{ gap: `${String(SLICE_GAP_PX)}px` }}
        role="img"
        aria-label={t('kpi.pmoFlow.composition.title')}
      >
        {slices.map((slice, index) => (
          <PhaseSlice
            key={slice.key}
            slice={slice}
            label={t(`kpi.pmoFlow.segment.${slice.key}`)}
            value={`${formatQuantile(slice.kind === 'measured' ? slice.width : undefined)}${daysSuffix}`}
            borderRadius={sliceRadius(index, slices.length)}
            isHighlighted={highlightedKey === slice.key}
            onHighlight={(key) => onHighlight?.(key)}
          />
        ))}
      </div>

      <div className={`relative ${LABEL_AREA_CLASS}`}>{renderTicks('below')}</div>
    </div>
  );
}
